import { useState } from 'react';
import * as ttsService from '../services/ttsService';
import * as audioService from '../services/audioService';

interface UseAudioProcessingProps {
  sessionId: string;
  tutorId: string;
  languageCode: string;
  voiceId: string;
  onResponse: (text: string) => void;
  onError: (error: string) => void;
  setVisualizerState: (state: 'idle' | 'speaking' | 'listening') => void;
  addMessage: (message: { text: string; type: 'user' | 'bot' }) => void;
  setIsWaitingForAI: (isWaiting: boolean) => void;
}

export const useAudioProcessing = ({
  sessionId,
  tutorId,
  languageCode,
  voiceId,
  onResponse,
  onError,
  setVisualizerState,
  addMessage,
  setIsWaitingForAI,
}: UseAudioProcessingProps) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const sendAudioToApi = async (audioBlob: Blob) => {
    try {
      setIsWaitingForAI(true);

      const { response: responseText, transcript: responseTranscript } = 
        await audioService.sendAudioMessage(audioBlob, sessionId, tutorId, languageCode);
      
      addMessage({
        text: responseTranscript,
        type: 'user'
      });
      
      addMessage({
        text: responseText,
        type: 'bot'
      });
      
      await handleTTSResponse(responseText);
      onResponse(responseText);
    } catch (error) {
      console.error('Error sending audio:', error);
      const errorMessage = 'Failed to process audio message';
      addMessage({
        text: errorMessage,
        type: 'bot'
      });
      onError(errorMessage);
    } finally {
      setIsWaitingForAI(false);
    }
  };

  const handleTTSResponse = async (text: string) => {
    try {
      const token = await ttsService.getTTSToken();
      setVisualizerState('speaking');

      await ttsService.synthesizeRealTimeSpeech(
        text,
        token,
        languageCode,
        {
          voice: voiceId,
          speed: 1.0,
          pitch: 0.0
        },
        () => {
          setVisualizerState('listening');
        }
      );
    } catch (error) {
      console.error('Error playing TTS:', error);
      onError('Failed to play audio response');
      setVisualizerState('listening');
    }
  };

  return {
    sendAudioToApi,
    audioUrl
  };
};
